// The import as a sequence: keep the original file, read what's here, merge, save with the revision
// check (retrying once if another device saved in between). The storage calls are passed in, so the same
// code runs in the app (through Supabase) and in the tests (straight against Postgres).
import { fromServer, mergeLedger, parseImport, type MergeSummary, type Parsed } from './importer';
import { docJson, type Ledger } from './ledger';
import { sanitize } from './state';
import { importDividends, type ImportResult } from './dividends';

export interface LoadResult {
  rev: number; savedAt: string | null; doc: Record<string, unknown> | null; history: unknown[]; intraday: unknown[];
  manualPrices: Record<string, unknown>; fxManual: unknown; fileExtra: Record<string, unknown>;
}
export interface SaveArgs {
  baseRev: number; doc: Record<string, unknown>; history: unknown[];
  hourly: unknown[]; manual: Record<string, unknown>; fxManual: unknown; fileExtra: Record<string, unknown>;
}
export type SaveResult = { ok: true; rev: number; savedAt: string } | { conflict: true; rev: number };
export interface LedgerStore {
  load(): Promise<LoadResult>;
  save(a: SaveArgs): Promise<SaveResult>;
  keepUpload(u: { filename: string; kind: string; sha256: string; content: string; summary: unknown }): Promise<void>;
}

export interface Preview { parsed: Parsed; summary: MergeSummary; merged: Ledger; baseRev: number; dividends?: ImportResult }

/** Works out what an import would change, without saving anything. */
export async function previewImport(store: LedgerStore, text: string): Promise<Preview> {
  const parsed = parseImport(text);
  const cur = await store.load();
  const current = fromServer(cur);
  if (parsed.kind === 'dividends') {
    // A dividend update file from the old app's daily check: applied with the same rules the old app
    // used when it found one in Drive (payments booked if held and not already recorded, future ones
    // kept as upcoming).
    const S = sanitize({ ...docJson(current.doc), history: [] });
    const res = importDividends(S, { events: parsed.dividendEvents });
    const merged: Ledger = { ...current, doc: { ...current.doc, income: S.income, upcoming: S.upcoming } };
    const { summary } = mergeLedger(current, merged);
    return { parsed, summary, merged, baseRev: cur.rev, dividends: res };
  }
  const { ledger, summary } = mergeLedger(current, parsed.ledger);
  return { parsed, summary, merged: ledger, baseRev: cur.rev };
}

/** Saves an import. The original file is stored first, so it's kept even if the save fails. */
export async function commitImport(store: LedgerStore, text: string, filename: string, sha256: string): Promise<{ rev: number; summary: MergeSummary }> {
  let p = await previewImport(store, text);
  await store.keepUpload({ filename, kind: p.parsed.kind, sha256, content: text, summary: { ...p.summary, rejected: p.parsed.rejected, fixedIds: p.parsed.fixedIds } });
  for (let attempt = 0; ; attempt++) {
    const r = await store.save({
      baseRev: p.baseRev, doc: docJson(p.merged.doc), history: p.merged.history, hourly: p.merged.intraday,
      manual: p.merged.manualPrices, fxManual: p.merged.fxManual, fileExtra: p.merged.fileExtra,
    });
    if ('ok' in r) return { rev: r.rev, summary: p.summary };
    if (attempt >= 1) throw new Error('Your ledger was changed on another device during the import. Please try again.');
    p = await previewImport(store, text);       // someone saved in between: merge into the newer copy
  }
}

export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
